/**
 * Reports landing page (manager+).
 *
 * For now: a single rep-performance widget with a small date-range
 * picker (and a few presets). Sales are bounced back to /deals — the
 * server enforces this too, but redirecting client-side avoids a
 * jarring 403 flash and keeps the URL guessable.
 *
 * The date controls are *local dates*; we translate them to UTC
 * timestamps (start-of-day to end-of-day) before sending. This is what
 * managers expect ("show me last week" means last week in *my*
 * timezone, not UTC).
 */

import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { BarChart3, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";

import { RepPerformance } from "../components/dashboard/RepPerformance";
import { useAuthStore } from "../lib/auth";
import { reportsApi } from "../lib/reportsApi";

export const Route = createFileRoute("/_app/reports/")({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && user.role !== "admin" && user.role !== "manager") {
      throw redirect({ to: "/deals" });
    }
  },
  component: ReportsPage,
});

type Preset = "7" | "30" | "90" | "custom";

function ReportsPage() {
  const today = useMemo(() => new Date(), []);
  const defaultSince = useMemo(() => addDays(today, -30), [today]);

  const [preset, setPreset] = useState<Preset>("30");
  const [sinceDate, setSinceDate] = useState<string>(toDateInput(defaultSince));
  const [untilDate, setUntilDate] = useState<string>(toDateInput(today));

  const { since, until } = useMemo(
    () => ({
      since: startOfLocalDayIso(sinceDate),
      until: endOfLocalDayIso(untilDate),
    }),
    [sinceDate, untilDate],
  );

  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ["report:rep-performance", { since, until }],
    queryFn: () => reportsApi.repPerformance({ since, until }),
    enabled: Boolean(since && until),
  });

  function applyPreset(next: Preset) {
    setPreset(next);
    if (next === "custom") return;
    const days = Number(next);
    const newSince = addDays(new Date(), -days);
    setSinceDate(toDateInput(newSince));
    setUntilDate(toDateInput(new Date()));
  }

  return (
    <div className="page-shell space-y-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <BarChart3 className="size-5 opacity-70" aria-hidden />
            Reports
          </h1>
          <p className="text-sm opacity-60">
            How each rep is performing. Open deals and overdue follow-ups are live counts;
            everything else is for the selected window.
          </p>
        </div>
        {isFetching && !isLoading && (
          <span className="inline-flex items-center gap-1.5 text-xs opacity-60">
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            Refreshing…
          </span>
        )}
      </header>

      <section className="space-y-3 rounded-xl border border-[var(--border)] p-3">
        <div
          role="tablist"
          aria-label="Quick range"
          className="inline-flex rounded-md border border-[var(--border)] p-0.5 text-sm"
        >
          <PresetTab active={preset === "7"} onClick={() => applyPreset("7")} label="Last 7 days" />
          <PresetTab
            active={preset === "30"}
            onClick={() => applyPreset("30")}
            label="Last 30 days"
          />
          <PresetTab
            active={preset === "90"}
            onClick={() => applyPreset("90")}
            label="Last 90 days"
          />
          <PresetTab
            active={preset === "custom"}
            onClick={() => applyPreset("custom")}
            label="Custom"
          />
        </div>

        <div className="flex flex-wrap items-end gap-3 text-xs">
          <DateField
            id="report-since"
            label="From"
            value={sinceDate}
            max={untilDate}
            onChange={(v) => {
              setSinceDate(v);
              setPreset("custom");
            }}
          />
          <DateField
            id="report-until"
            label="To"
            value={untilDate}
            min={sinceDate}
            max={toDateInput(new Date())}
            onChange={(v) => {
              setUntilDate(v);
              setPreset("custom");
            }}
          />
          <button
            type="button"
            onClick={() => refetch()}
            className="ml-auto rounded-md border border-[var(--border)] px-3 py-1.5 text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5"
          >
            Refresh
          </button>
        </div>
      </section>

      {isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : error ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          Couldn't load report: {(error as Error).message}
        </div>
      ) : data ? (
        <>
          <p className="text-xs opacity-60">
            Window: {formatRange(data.since, data.until)} · {data.items.length}{" "}
            {data.items.length === 1 ? "rep" : "reps"}
          </p>
          <RepPerformance items={data.items} />
        </>
      ) : null}
    </div>
  );
}

function PresetTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`rounded px-3 py-1 transition-colors ${
        active
          ? "bg-[var(--accent)] text-white"
          : "opacity-70 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/5"
      }`}
    >
      {label}
    </button>
  );
}

function DateField({
  id,
  label,
  value,
  onChange,
  min,
  max,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  min?: string;
  max?: string;
}) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1 text-xs">
      <span className="opacity-70">{label}</span>
      <input
        id={id}
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
      />
    </label>
  );
}

// ── Date helpers ────────────────────────────────────────────────────────────

function addDays(d: Date, days: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

/** "YYYY-MM-DD" in the *local* timezone (matches `<input type="date">` value). */
function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Local 00:00 of the given YYYY-MM-DD as an ISO8601 UTC string. */
function startOfLocalDayIso(yyyyMmDd: string): string {
  const { y, m, d } = parseYmd(yyyyMmDd);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

/** Local 23:59:59.999 of the given YYYY-MM-DD as an ISO8601 UTC string. */
function endOfLocalDayIso(yyyyMmDd: string): string {
  const { y, m, d } = parseYmd(yyyyMmDd);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

function parseYmd(yyyyMmDd: string): { y: number; m: number; d: number } {
  const parts = yyyyMmDd.split("-");
  // `<input type="date">` always emits a well-formed string, but we still
  // want a runtime guard so TS narrows correctly and a manually-edited
  // value can't crash the page.
  if (parts.length !== 3) throw new Error(`Bad date string: ${yyyyMmDd}`);
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    throw new Error(`Bad date string: ${yyyyMmDd}`);
  }
  return { y, m, d };
}

function formatRange(sinceIso: string, untilIso: string): string {
  const fmt: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };
  return `${new Date(sinceIso).toLocaleDateString(undefined, fmt)} → ${new Date(
    untilIso,
  ).toLocaleDateString(undefined, fmt)}`;
}
